import { createClient } from '@supabase/supabase-js';

const supabaseUrl = (import.meta as { env?: { VITE_SUPABASE_URL?: string } }).env?.VITE_SUPABASE_URL || 'https://placeholder.supabase.co';
const supabaseAnonKey = (import.meta as { env?: { VITE_SUPABASE_ANON_KEY?: string } }).env?.VITE_SUPABASE_ANON_KEY || 'placeholder-key';

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export interface UserProfile {
  id: string;
  email: string;
  createCredits: number;
  user_metadata?: {
    createCredits?: number;
  };
}

import { PromptKit } from '../types';

export interface SessionMetadata {
  seed?: PromptKit;
}

export interface BeehiveSession {
  id: string;
  user_id: string;
  meta: SessionMetadata;
  created_at: string;
  updated_at: string;
}

export async function getUserProfile(): Promise<UserProfile | null> {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error || !user) return null;
    
    return {
      id: user.id,
      email: user.email || '',
      createCredits: user.user_metadata?.createCredits || 12,
      user_metadata: user.user_metadata
    };
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
}

export const getUserCredits = async (userId: string): Promise<number> => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('createCredits')
      .eq('id', userId)
      .single();
    if (error) throw error;
    return data.createCredits || 12;
  } catch (error) {
    console.warn('Could not fetch credits from database:', error);
    return 12;
  }
};

export const checkUserCredits = async (requiredCredits: number = 1): Promise<boolean> => {
  const availableCredits = await getUserCredits('placeholder-user-id');
  return availableCredits >= requiredCredits;
};

export const debitUserCredits = async (_amount: number = 1): Promise<boolean> => {
  // Placeholder implementation - always succeeds
  return true;
};

export const updateSessionMeta = async (sessionId: string, seed: PromptKit): Promise<void> => {
  try {
    const { error } = await supabase
      .from('sessions')
      .update({ meta: { seed } })
      .eq('id', sessionId);
    if (error) throw error;
  } catch (error) {
    console.warn('Could not update session meta:', error);
    throw error;
  }
};

export async function saveSessionSeed(sessionId: string, seed: PromptKit): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('sessions')
      .upsert({
        id: sessionId,
        meta: { seed },
        updated_at: new Date().toISOString()
      });
    
    return !error;
  } catch (error) {
    console.error('Error saving session seed:', error);
    return false;
  }
}
export default supabase;
